// src/pages/languages/CreateLanguageCategory.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Image as ImageIcon, Save } from "lucide-react";
import { languageCategoriesAPI } from "../../services/languageService";

export default function CreateLanguageCategory() {
  // إنشاء: /dashboard/languages/:languageId/categories/create
  // تعديل: /dashboard/languages/categories/:categoryId/edit
  const { languageId, categoryId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(categoryId);

  const [form, setForm] = useState({
    name: "",
    description: "",
    order: 0,
    is_active: true,
  });
  const [iconFile, setIconFile] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [resolvedLanguageId, setResolvedLanguageId] = useState(languageId);

  useEffect(() => {
    if (isEdit) fetchCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const data = await languageCategoriesAPI.getOne(categoryId);
      setForm({
        name: data.name || "",
        description: data.description || "",
        order: data.order ?? 0,
        is_active: data.is_active ?? true,
      });
      setResolvedLanguageId(data.language);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (iconFile) payload.icon = iconFile;

      if (isEdit) {
        await languageCategoriesAPI.update(categoryId, payload);
        navigate(`/dashboard/languages/categories/${categoryId}/skills`);
      } else {
        const res = await languageCategoriesAPI.create(languageId, payload);
        navigate(`/dashboard/languages/${languageId}/categories`);
      }
    } catch (err) {
      setError(
        err?.response?.data
          ? JSON.stringify(err.response.data)
          : "حدث خطأ أثناء الحفظ"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <button
          onClick={() =>
            navigate(
              isEdit
                ? `/dashboard/languages/categories/${categoryId}/skills`
                : `/dashboard/languages/${languageId}/categories`
            )
          }
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowRight className="w-4 h-4" />
          رجوع
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? "تعديل الكاتيجوري" : "إضافة كاتيجوري جديد"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-4">
        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            اسم الكاتيجوري
          </label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            الوصف
          </label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الترتيب
            </label>
            <input
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: Number(e.target.value) })
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              مفعّل
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            أيقونة الكاتيجوري
          </label>
          <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-500 cursor-pointer hover:bg-gray-50">
            <ImageIcon className="w-4 h-4" />
            {iconFile ? iconFile.name : "اختر صورة"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setIconFile(e.target.files?.[0] || null)}
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-60"
        >
          <Save className="w-4 h-4" />
          {saving ? "جارٍ الحفظ..." : "حفظ"}
        </button>
      </form>
    </div>
  );
}